import { useMemo, useState } from "react";
import useUserLibrary from "api/UserResource/useUserLibrary";
import { CustomPaginator } from "components";
import BookCoverPreview from "components/BookCoverPreview";
import { DataTable } from "components/PortalDataTable";
import { Column } from "primereact/column";
import { IoDocumentsOutline, IoLibraryOutline, IoStatsChartOutline } from "react-icons/io5";

const UserLibrary = ({ userId }) => {
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(20);
	const [firstPage, setFirstPage] = useState(0);

	const { data: libraryBooks, isLoading } = useUserLibrary(userId, {
		page,
		rows_per_page: rowsPerPage,
	});


	const books = useMemo(
		() => libraryBooks?.data?.records || [],
		[libraryBooks?.data?.records]
	);

	const libraryStats = useMemo(() => {
		if (!books.length) {
			return { total: 0, purchased: 0, subscription: 0 };
		}
		return {
			total: books.length,
			purchased: books.filter((book) => book.is_purchased).length,
			subscription: books.filter((book) => book.in_library).length,
		};
	}, [books]);

	const disableRowsPerPageChanger = useMemo(() => {
		if (parseInt(page) === parseInt(libraryBooks?.data?.total_pages) && parseInt(page) !== 1) {
			return true;
		}
		return false;
	}, [page, libraryBooks]);

	const handleChangeRowsPerPage = (value) => {
		if (value === rowsPerPage) return;
		setRowsPerPage(value);
		setPage(1);
		setFirstPage(0);
	};

	// --- Column body templates ---

	const coverTemplate = (rowData) => {
		return (
			<BookCoverPreview
				coverUrl={rowData?.book_cover?.url}
				title={rowData?.title}
				thumbnailWidth={40}
				thumbnailHeight={60}
			/>
		);
	};

	const categoryTemplate = (rowData) => {
		return rowData?.category?.name || "--";
	};

	if (books.length === 0 && !isLoading) {
		return (
			<div className="w-full flex flex-col items-center justify-center py-12">
				<div className="p-4 bg-gray-100 rounded-full mb-4">
					<IoDocumentsOutline size={64} className="text-gray-400" />
				</div>
				<h3 className="text-lg font-semibold text-gray-900 mb-2">Library is empty</h3>
				<p className="text-gray-600 text-center max-w-md">
					This user has no purchased or subscribed books in their library.
				</p>
			</div>
		);
	}

	return (
		<div className="w-full space-y-6">
			{/* Header with stats */}
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-blue-100 rounded-lg">
						<IoLibraryOutline className="w-5 h-5 text-blue-600" />
					</div>
					<div>
						<h3 className="text-lg font-semibold text-gray-900">User Library</h3>
						<p className="text-sm text-gray-600">Books purchased or added via subscription</p>
					</div>
				</div>

				<div className="flex gap-3 text-sm">
					<div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
						<IoStatsChartOutline className="w-4 h-4 text-green-600" />
						<span className="font-medium text-green-800">
							{libraryBooks?.data?.total_records || libraryStats.total} Total
						</span>
					</div>

					{libraryStats.purchased > 0 && (
						<div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg text-xs">
							<span className="font-medium text-blue-800">{libraryStats.purchased} Purchased</span>
						</div>
					)}

					{libraryStats.subscription > 0 && (
						<div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg text-xs">
							<span className="font-medium text-red-800">
								{libraryStats.subscription} Subscribed
							</span>
						</div>
					)}
				</div>
			</div>

			{/* Controls */}
			<div className="flex items-center justify-between">
				<p className="text-sm text-gray-600">
					Showing {books.length} of {libraryBooks?.data?.total_records || 0} books
				</p>

				<div className="flex items-center gap-2">
					<span className="text-sm text-gray-600">Show</span>
					<select
						name="rowsPerPage"
						id="rowsPerPage"
						value={rowsPerPage}
						onChange={(e) => handleChangeRowsPerPage(e.target.value)}
						disabled={disableRowsPerPageChanger}
						className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
					>
						<option value="10">10</option>
						<option value="20">20</option>
						<option value="40">40</option>
						<option value="50">50</option>
					</select>
				</div>
			</div>

			{/* Book table */}
			<DataTable
				value={books}
				loading={isLoading}
				dataKey="id"
				tableClassName="font-sans text-sm"
				autoLayout
				emptyMessage="No books found"
			>
				<Column header="Cover" body={coverTemplate} style={{ width: "60px" }} />
				<Column field="title" header="Title" />
				<Column field="author" header="Author" />
				<Column header="Category" body={categoryTemplate} />
			</DataTable>

			{/* Pagination */}
			{libraryBooks?.data?.total_pages > 1 && (
				<div className="pt-6 border-t border-gray-200">
					<CustomPaginator
						firstPage={firstPage}
						setFirstPage={setFirstPage}
						rowsPerPage={rowsPerPage}
						setRowsPerPage={setRowsPerPage}
						totalRecords={libraryBooks?.data?.total_records}
						setPage={setPage}
						showRowsPerPageDropdown={false}
					/>
				</div>
			)}
		</div>
	);
};

export default UserLibrary;
